/**
 * TeamEventLogPanel — log team events that tighten the next regenerate of
 * the Team Blueprint. Per Feature #5 spec §living-document logic.
 */

import { useEffect, useState } from "react";
import { Callout } from "./index.js";

const EVENT_KINDS = [
  { value: "decision_made",      label: "Decision made" },
  { value: "conflict_surfaced",  label: "Conflict surfaced" },
  { value: "milestone_hit",      label: "Milestone hit" },
  { value: "milestone_missed",   label: "Milestone missed" },
  { value: "member_added",       label: "Member added" },
  { value: "member_removed",     label: "Member removed" },
  { value: "external_event",     label: "External event" },
];

export default function TeamEventLogPanel({ blueprintId, bearerToken }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ event_kind: "decision_made", description: "", occurred_at: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const refresh = async () => {
    setLoading(true);
    try {
      const res = await fetch(`/.netlify/functions/team-event-log?blueprint_id=${blueprintId}`, {
        headers: { authorization: `Bearer ${bearerToken}` },
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || "Failed to load");
      else setEvents(body.events || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { if (blueprintId && bearerToken) refresh(); }, [blueprintId, bearerToken]);

  const submit = async () => {
    if (!form.description.trim()) { setError("Description is required."); return; }
    setBusy(true);
    setError(null);
    try {
      const res = await fetch("/.netlify/functions/team-event-log", {
        method: "POST",
        headers: { "content-type": "application/json", authorization: `Bearer ${bearerToken}` },
        body: JSON.stringify({
          blueprint_id: blueprintId,
          event_kind: form.event_kind,
          description: form.description,
          occurred_at: form.occurred_at || null,
        }),
      });
      const body = await res.json();
      if (!res.ok) setError(body.error || "Log failed");
      else {
        setShowForm(false);
        setForm({ event_kind: "decision_made", description: "", occurred_at: "" });
        refresh();
      }
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-component="TeamEventLogPanel" style={panelStyle}>
      <div style={{
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "16px",
        letterSpacing: "var(--ppr-typography-letter-spacing-wide)",
        textTransform: "uppercase",
        color: "var(--ppr-color-accent-navy)",
        marginBottom: 10,
      }}>
        Team event log
      </div>

      <Callout.Tip>
        Each event tightens the next regenerate of this Team Blueprint. Decisions, conflicts surfaced, milestones, member changes, external events.
      </Callout.Tip>

      {loading && <p style={{ color: "var(--ppr-color-text-muted)" }}>Loading…</p>}

      {!loading && events.length === 0 && (
        <p style={{ color: "var(--ppr-color-text-secondary)", fontSize: "var(--ppr-typography-font-size-sm)" }}>
          No events logged yet.
        </p>
      )}

      <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 10 }}>
        {events.map((e) => (
          <article key={e.event_id} style={{ padding: "10px 12px", border: "1px solid var(--ppr-color-border-default)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginBottom: 4 }}>
              <span style={{
                fontFamily: "var(--ppr-typography-font-family-display)",
                fontSize: "12px",
                letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
                textTransform: "uppercase",
                color: "var(--ppr-color-accent-navy)",
              }}>
                {e.event_kind.replace(/_/g, " ")}
              </span>
              <span style={{ fontFamily: "var(--ppr-typography-font-family-mono)", fontSize: "10.5px", color: "var(--ppr-color-text-muted)" }}>
                {new Date(e.logged_at).toLocaleString()}
              </span>
            </div>
            <div style={{ fontFamily: "var(--ppr-typography-font-family-body)", fontSize: "var(--ppr-typography-font-size-base)", lineHeight: "var(--ppr-typography-line-height-relaxed)" }}>
              {e.description}
            </div>
          </article>
        ))}
      </div>

      {error && (
        <div style={{
          marginTop: 10,
          padding: "10px 12px",
          border: "2px solid var(--ppr-color-accent-coral)",
          background: "var(--ppr-color-accent-coral-very-soft)",
          color: "var(--ppr-color-accent-coral-dark)",
          fontSize: "var(--ppr-typography-font-size-sm)",
        }}>
          {error}
        </div>
      )}

      {!showForm ? (
        <button onClick={() => setShowForm(true)} style={{ ...ghostBtn, marginTop: 14 }}>+ Log event</button>
      ) : (
        <div style={{ marginTop: 14, display: "grid", gap: 10 }}>
          <Field label="Event kind">
            <select value={form.event_kind} onChange={(e) => setForm({ ...form, event_kind: e.target.value })} style={selectStyle}>
              {EVENT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </Field>
          <Field label="Description">
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows={3}
              style={inputStyle}
              placeholder="What happened? Who was involved? What did the team's response look like?"
            />
          </Field>
          <Field label="Occurred at (optional)">
            <input type="datetime-local" value={form.occurred_at} onChange={(e) => setForm({ ...form, occurred_at: e.target.value })} style={inputStyle} />
          </Field>
          <div style={{ display: "flex", gap: 10 }}>
            <button onClick={submit} disabled={busy} style={primaryBtn}>{busy ? "Logging…" : "Log it"}</button>
            <button onClick={() => setShowForm(false)} disabled={busy} style={ghostBtn}>Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}

const panelStyle = {
  marginTop: 24,
  padding: "16px 18px",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
};
const inputStyle = {
  padding: "10px 12px",
  fontFamily: "var(--ppr-typography-font-family-ui)",
  fontSize: "var(--ppr-typography-font-size-base)",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-primary)",
  width: "100%",
  outline: "none",
  resize: "vertical",
};
const selectStyle = { ...inputStyle, cursor: "pointer" };
const primaryBtn = {
  padding: "10px 16px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "var(--ppr-typography-font-size-sm)",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-accent-navy)",
  color: "var(--ppr-color-text-inverse)",
  cursor: "pointer",
};
const ghostBtn = {
  padding: "8px 14px",
  fontFamily: "var(--ppr-typography-font-family-display)",
  fontSize: "11px",
  letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
  textTransform: "uppercase",
  border: "1px solid var(--ppr-color-border-default)",
  background: "var(--ppr-color-background-surface)",
  color: "var(--ppr-color-text-secondary)",
  cursor: "pointer",
};
function Field({ label, children }) {
  return (
    <div>
      <label style={{
        display: "block",
        fontFamily: "var(--ppr-typography-font-family-display)",
        fontSize: "var(--ppr-typography-font-size-sm)",
        letterSpacing: "var(--ppr-typography-letter-spacing-wider)",
        textTransform: "uppercase",
        color: "var(--ppr-color-text-secondary)",
        marginBottom: 6,
      }}>{label}</label>
      {children}
    </div>
  );
}
